/**
 * @module RecruitingReportsUnified
 * @description Unified Recruiting Reports — Übersicht, Fest-Pipeline, Flex-Einsätze, Quellen.
 * @route /recruiting/reports
 * @since v2.246.0
 */
import { useState, useEffect, useCallback } from 'react';
import useTabParam from '../hooks/useTabParam';
import { useLanguage } from '../context/LanguageContext';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { Users, UserPlus, Briefcase, Search } from 'lucide-react';

const API = import.meta.env.VITE_API_URL || '';
const f = (url) => fetch(`${API}${url}`, { credentials: 'include' });

const formatMonat = (m) => {
  if (!m) return m;
  const [y, mo] = m.split('-');
  const date = new Date(parseInt(y), parseInt(mo) - 1);
  return date.toLocaleDateString('de-AT', { month: 'long', year: 'numeric' });
};

export default function RecruitingReportsUnified() {
  const { t } = useLanguage();
  const [activeTab, setActiveTab] = useTabParam('uebersicht');
  const [dashboard, setDashboard] = useState(null);
  const [flexDash, setFlexDash] = useState(null);
  const [flexReports, setFlexReports] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dRes, fRes, frRes] = await Promise.all([
        f('/api/recruiting/dashboard'),
        f('/api/flex/dashboard?periode=90'),
        f('/api/flex/reports/einsaetze'),
      ]);
      if (dRes.ok) setDashboard(await dRes.json());
      if (fRes.ok) setFlexDash(await fRes.json());
      if (frRes.ok) setFlexReports(await frRes.json());
    } catch { /* */ }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const tabs = [
    { key: 'uebersicht', label: t('recruiting.reports.uebersicht', 'Übersicht') },
    { key: 'fest', label: t('recruiting.reports.fest', 'Fest-Pipeline') },
    { key: 'flex', label: t('recruiting.reports.flex', 'Flex-Einsätze') },
    { key: 'quellen', label: t('recruiting.reports.quellen', 'Quellen') },
    { key: 'kosten', label: t('recruiting.reports.kosten', 'Kosten') },
  ];

  const dk = dashboard?.kpis || {};
  const fk = flexDash?.kpis || {};
  const funnel = dashboard?.funnel || [];
  const quellen = dashboard?.quellen || [];

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="bg-[var(--bg-card,#fff)] border border-[var(--border-card,#E5E7EB)] rounded-lg p-2">
        <p className="text-xs font-medium" style={{ color: 'var(--text-primary)' }}>{payload[0].payload.name}: {payload[0].value}</p>
      </div>
    );
  };

  return (
    <div className="p-6 space-y-6">
      <div>
        <h1 className="text-xl font-bold" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
          {t('recruiting.reports.title', 'Recruiting Reports')}
        </h1>
        <p className="text-sm mt-1" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
          {t('recruiting.reports.subtitle', 'Analysen und Statistiken über den Recruiting-Prozess')}
        </p>
      </div>

      {/* Tab bar */}
      <div className="flex items-center gap-1 bg-[var(--bg-inset,#F8F9FC)] rounded-[10px] p-[3px] border border-[var(--border-default,#E5E7EB)] w-fit">
        {tabs.map(tb => (
          <button key={tb.key} onClick={() => setActiveTab(tb.key)}
            className={`px-3 py-1.5 rounded-[8px] text-xs font-medium transition-all ${
              activeTab === tb.key ? 'bg-white shadow-sm font-semibold dark:bg-[var(--bg-card)]' : ''
            }`}
            style={{ color: activeTab === tb.key ? 'var(--text-primary)' : 'var(--text-secondary)' }}>
            {tb.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="p-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>{t('common.loading', 'Laden...')}</div>
      ) : (
        <>
          {/* Übersicht */}
          {activeTab === 'uebersicht' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: t('recruiting.reports.gesamtBewerber', 'Gesamt Bewerber'), value: (dk.gesamt || 0) + (fk.pool_size || 0) + (fk.pipeline || 0), icon: Users, color: '#EC4899' },
                  { label: t('recruiting.reports.festPipeline', 'Fest in Pipeline'), value: dk.pipeline || 0, icon: UserPlus, color: '#EC4899' },
                  { label: t('recruiting.reports.flexPool', 'Flex-Pool Größe'), value: fk.pool_size || 0, icon: Briefcase, color: '#8B5CF6' },
                  { label: t('recruiting.reports.offeneStellen', 'Offene Stellen'), value: dk.offene_stellen ?? 0, icon: Search, color: '#338edc' },
                ].map((kpi, i) => (
                  <div key={i} className="bg-[var(--bg-card,#fff)] rounded-xl border p-4" style={{ borderColor: 'var(--border-card)' }}>
                    <div className="flex items-center gap-2 mb-1">
                      <kpi.icon size={16} style={{ color: kpi.color }} />
                      <p className="text-xs font-medium uppercase tracking-wider" style={{ color: 'var(--text-tertiary)' }}>{kpi.label}</p>
                    </div>
                    <p className="text-2xl font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>{kpi.value}</p>
                  </div>
                ))}
              </div>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Monthly trend */}
                <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card)' }}>
                  <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary)' }}>
                    {t('recruiting.reports.monatlich', 'Monatlicher Trend')}
                  </h3>
                  {(dashboard?.monatlich || []).length > 0 ? (
                    <ResponsiveContainer width="100%" height={200}>
                      <BarChart data={dashboard.monatlich.map(m => ({ name: formatMonat(m.monat), eingegangen: parseInt(m.eingegangen), eingestellt: parseInt(m.eingestellt) }))}>
                        <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'var(--text-tertiary, #6B7280)' }} />
                        <YAxis tick={{ fontSize: 11, fill: 'var(--text-tertiary, #6B7280)' }} />
                        <Tooltip content={<CustomTooltip />} />
                        <Bar dataKey="eingegangen" fill="#338edc" radius={[3, 3, 0, 0]} />
                        <Bar dataKey="eingestellt" fill="#10B981" radius={[3, 3, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <p className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>{t('recruiting.reports.noData', 'Keine Daten')}</p>
                  )}
                </div>
                {/* Quellen summary */}
                <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card)' }}>
                  <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary)' }}>
                    {t('recruiting.reports.quellenSummary', 'Top Quellen')}
                  </h3>
                  <div className="space-y-2">
                    {quellen.slice(0, 5).map(q => {
                      const maxQ = Math.max(...quellen.map(x => parseInt(x.count)), 1);
                      const convPct = parseInt(q.count) > 0 ? Math.round((parseInt(q.eingestellt) / parseInt(q.count)) * 100) : 0;
                      return (
                        <div key={q.quelle} className="flex items-center gap-3">
                          <span className="text-xs w-20 text-right flex-shrink-0 capitalize font-medium" style={{ color: 'var(--text-secondary)' }}>{q.quelle}</span>
                          <div className="flex-1 h-5 rounded overflow-hidden" style={{ background: 'var(--bg-inset)' }}>
                            <div className="h-full rounded" style={{ width: `${Math.max((parseInt(q.count) / maxQ) * 100, 4)}%`, background: '#338edc' }} />
                          </div>
                          <span className="text-xs font-semibold w-6 text-right tabular-nums" style={{ color: 'var(--text-primary)' }}>{q.count}</span>
                          <span className="text-xs w-12 text-right" style={{ color: convPct > 0 ? '#10B981' : 'var(--text-muted)' }}>{convPct}%</span>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Fest-Pipeline */}
          {activeTab === 'fest' && (
            <div className="space-y-6">
              <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card)' }}>
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary)' }}>
                  {t('recruiting.reports.pipelineFunnel', 'Pipeline-Funnel')}
                </h3>
                <div className="space-y-2">
                  {funnel.map((s, i, arr) => {
                    const maxCount = Math.max(...funnel.map(x => parseInt(x.count)), 1);
                    const pct = (parseInt(s.count) / maxCount) * 100;
                    const prev = i > 0 ? arr[i - 1] : null;
                    const convPct = prev && parseInt(prev.count) > 0 ? Math.round((parseInt(s.count) / parseInt(prev.count)) * 100) : null;
                    return (
                      <div key={s.id || i} className="flex items-center gap-3">
                        <span className="text-xs w-32 text-right flex-shrink-0 font-medium truncate" style={{ color: 'var(--text-secondary)' }}>{s.name}</span>
                        <div className="flex-1 h-7 rounded-lg overflow-hidden" style={{ background: 'var(--bg-inset)' }}>
                          <div className="h-full rounded-lg flex items-center px-2" style={{ width: `${Math.max(pct, 4)}%`, background: s.farbe || '#338edc' }}>
                            <span className="text-xs font-bold text-white">{s.count}</span>
                          </div>
                        </div>
                        {convPct !== null && <span className="text-xs w-12 text-right" style={{ color: 'var(--text-muted)' }}>{convPct}%</span>}
                      </div>
                    );
                  })}
                </div>
              </div>
              <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                {[
                  { label: t('recruiting.reports.avgTimeToHire', 'Ø Time-to-Hire'), value: `${dk.avg_time_to_hire_tage || 0} ${t('recruiting.reports.tage', 'Tage')}` },
                  { label: t('recruiting.reports.eingestelltMonat', 'Eingestellt (30T)'), value: dk.eingestellt_monat || 0 },
                  { label: t('recruiting.reports.offeneStellen', 'Offene Stellen'), value: dk.offene_stellen ?? 0 },
                ].map((kpi, i) => (
                  <div key={i} className="bg-[var(--bg-card,#fff)] rounded-xl border p-4" style={{ borderColor: 'var(--border-card)' }}>
                    <p className="text-xs font-medium uppercase tracking-wider mb-1" style={{ color: 'var(--text-tertiary)' }}>{kpi.label}</p>
                    <p className="text-xl font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>{kpi.value}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Flex-Einsätze */}
          {activeTab === 'flex' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: t('recruiting.reports.flexPoolSize', 'Pool-Größe'), value: fk.pool_size || 0 },
                  { label: t('recruiting.reports.flexEinsaetze', 'Einsätze'), value: fk.einsaetze_count || 0 },
                  { label: t('recruiting.reports.flexZusageQuote', 'Zusage-Quote'), value: `${Math.round((fk.zusage_quote || 0) * 100)}%` },
                  { label: t('recruiting.reports.flexKosten', 'Kosten'), value: `€${(fk.kosten || 0).toLocaleString('de-AT')}` },
                ].map((kpi, i) => (
                  <div key={i} className="bg-[var(--bg-card,#fff)] rounded-xl border p-4" style={{ borderColor: 'var(--border-card)' }}>
                    <p className="text-xs font-medium uppercase tracking-wider mb-1" style={{ color: 'var(--text-tertiary)' }}>{kpi.label}</p>
                    <p className="text-xl font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>{kpi.value}</p>
                  </div>
                ))}
              </div>
              {/* Export button */}
              <div className="flex gap-3">
                {['einsaetze', 'worker'].map(type => (
                  <a key={type} href={`${API}/api/flex/reports/export/${type}`} target="_blank" rel="noopener noreferrer"
                    className="px-4 py-2 rounded-xl border text-sm font-medium inline-flex items-center gap-2"
                    style={{ borderColor: 'var(--border-card)', color: 'var(--text-secondary)' }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    {type === 'einsaetze' ? t('recruiting.reports.exportEinsaetze', 'Einsätze exportieren') : t('recruiting.reports.exportWorker', 'Worker exportieren')}
                  </a>
                ))}
              </div>
            </div>
          )}

          {/* Quellen */}
          {activeTab === 'quellen' && (
            <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card)' }}>
              <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary)' }}>
                {t('recruiting.reports.quellenAnalyse', 'Quellen-Analyse (Fest + Flex)')}
              </h3>
              <div className="space-y-3">
                {quellen.map(q => {
                  const maxQ = Math.max(...quellen.map(x => parseInt(x.count)), 1);
                  const convPct = parseInt(q.count) > 0 ? Math.round((parseInt(q.eingestellt) / parseInt(q.count)) * 100) : 0;
                  return (
                    <div key={q.quelle} className="flex items-center gap-3">
                      <span className="text-xs w-24 text-right flex-shrink-0 capitalize font-medium" style={{ color: 'var(--text-secondary)' }}>{q.quelle}</span>
                      <div className="flex-1 h-6 rounded overflow-hidden" style={{ background: 'var(--bg-inset)' }}>
                        <div className="h-full rounded flex items-center px-2" style={{ width: `${Math.max((parseInt(q.count) / maxQ) * 100, 4)}%`, background: '#338edc' }}>
                          <span className="text-[10px] font-bold text-white">{q.count}</span>
                        </div>
                      </div>
                      <span className="text-xs font-semibold w-20 text-right" style={{ color: 'var(--text-primary)' }}>
                        {q.eingestellt} {t('recruiting.reports.eingestellt', 'eingest.')}
                      </span>
                      <span className="text-xs w-12 text-right font-medium" style={{ color: convPct > 0 ? '#10B981' : 'var(--text-muted)' }}>
                        {convPct}%
                      </span>
                    </div>
                  );
                })}
                {quellen.length === 0 && (
                  <p className="py-8 text-center text-sm" style={{ color: 'var(--text-muted)' }}>{t('recruiting.reports.noData', 'Keine Daten')}</p>
                )}
              </div>
            </div>
          )}

          {/* Kosten */}
          {activeTab === 'kosten' && (
            <div className="space-y-6">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                {[
                  { label: t('recruiting.reports.flexKostenGesamt', 'Flex-Kosten (Gesamt)'), value: `€${(fk.kosten || 0).toLocaleString('de-AT')}` },
                  { label: t('recruiting.reports.flexEinsaetzeCount', 'Einsätze (Gesamt)'), value: fk.einsaetze_count || 0 },
                  { label: t('recruiting.reports.flexPoolGroesse', 'Pool-Größe'), value: fk.pool_size || 0 },
                  { label: t('recruiting.reports.festOffenStellen', 'Offene Stellen'), value: dk.offene_stellen ?? 0 },
                ].map((kpi, i) => (
                  <div key={i} className="bg-[var(--bg-card,#fff)] rounded-xl border p-4" style={{ borderColor: 'var(--border-card)' }}>
                    <p className="text-xs font-medium uppercase tracking-wider mb-1" style={{ color: 'var(--text-tertiary)' }}>{kpi.label}</p>
                    <p className="text-xl font-bold tabular-nums" style={{ color: 'var(--text-primary)' }}>{kpi.value}</p>
                  </div>
                ))}
              </div>

              {/* Flex cost breakdown */}
              <div className="bg-[var(--bg-card,#fff)] rounded-xl border p-5" style={{ borderColor: 'var(--border-card)' }}>
                <h3 className="text-sm font-semibold mb-4" style={{ color: 'var(--text-secondary)' }}>
                  {t('recruiting.reports.kostenUebersicht', 'Kosten-Übersicht')}
                </h3>
                <div className="space-y-3">
                  {[
                    { label: t('recruiting.reports.flexPersonalkosten', 'Flex-Personalkosten'), value: fk.kosten || 0, pct: 100 },
                    { label: t('recruiting.reports.kostenProEinsatz', 'Ø Kosten pro Einsatz'), value: fk.einsaetze_count > 0 ? Math.round((fk.kosten || 0) / fk.einsaetze_count) : 0, pct: 60 },
                    { label: t('recruiting.reports.kostenProWorker', 'Ø Kosten pro Worker'), value: fk.pool_size > 0 ? Math.round((fk.kosten || 0) / fk.pool_size) : 0, pct: 40 },
                  ].map((item, i) => (
                    <div key={i} className="flex items-center gap-3">
                      <span className="text-xs w-40 text-right flex-shrink-0 font-medium" style={{ color: 'var(--text-secondary)' }}>{item.label}</span>
                      <div className="flex-1 h-5 rounded overflow-hidden" style={{ background: 'var(--bg-inset)' }}>
                        <div className="h-full rounded" style={{ width: `${Math.max(item.pct, 4)}%`, background: '#338edc' }} />
                      </div>
                      <span className="text-xs font-semibold w-16 text-right tabular-nums" style={{ color: 'var(--text-primary)' }}>
                        €{item.value.toLocaleString('de-AT')}
                      </span>
                    </div>
                  ))}
                </div>
              </div>

              <p className="text-xs" style={{ color: 'var(--text-muted)' }}>
                {t('recruiting.reports.kostenHinweis', 'Hinweis: Recruiting-Kosten (Stellenausschreibungen, Plattformen) werden separat in der Personalplanung erfasst.')}
              </p>
            </div>
          )}
        </>
      )}
    </div>
  );
}
